import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, FlatList, ActivityIndicator, Picker } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager, { MessageSource } from '../services/ListsManager';

const FilterScreen = ({ onClose, isModal, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const state = location.state || {};

  // Get filter parameters from props or location state
  const listId = props.listId || state.listId;
  const initialSelectedUsers = props.selectedUsers || state.selectedUsers;
  const initialFromDate = props.fromDate || state.fromDate;
  const initialToDate = props.toDate || state.toDate;
  const initialDateFilter = props.dateFilter || state.dateFilter;
  const initialSelectedSources = props.selectedSources || state.selectedSources;

  // State for filters
  const [selectedUsers, setSelectedUsers] = useState(initialSelectedUsers || []);
  const [fromDate, setFromDate] = useState(initialFromDate ? new Date(initialFromDate) : null);
  const [toDate, setToDate] = useState(initialToDate ? new Date(initialToDate) : null);
  const [dateFilter, setDateFilter] = useState(initialDateFilter || null);
  const [selectedSources, setSelectedSources] = useState(initialSelectedSources || []);

  // State for filter options
  const [twitterUsers, setTwitterUsers] = useState([]);
  const [telegramChannels, setTelegramChannels] = useState([]);
  const [tiktokChannels, setTiktokChannels] = useState([]);
  const [instagramProfiles, setInstagramProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Available date filter options — keys match handleDateFilterSelect switch cases
  const dateFilterOptions = [
    { key: 'Today', label: t('app.filter.today') },
    { key: 'Last 7 days', label: t('app.filter.last7days') },
    { key: 'Last 14 days', label: t('app.filter.last14days') },
    { key: 'Last 30 days', label: t('app.filter.last30days') },
    { key: 'Custom', label: t('app.filter.custom') },
  ];

  // Available source options
  const sourceOptions = [
    { id: MessageSource.TWITTER, label: t('app.filter.twitter') },
    { id: MessageSource.TELEGRAM, label: t('app.filter.telegram') },
    { id: MessageSource.TIKTOK, label: t('app.filter.tiktok') },
    { id: MessageSource.INSTAGRAM, label: t('app.filter.instagram') },
    { id: MessageSource.FACEBOOK_GROUP, label: 'Facebook Groups' },
    { id: MessageSource.TV, label: t('app.filter.tv') },
  ];

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let twitterUsernames = [];
      let telegramChannelsList = [];
      let tiktokChannelsList = [];
      let instagramProfilesList = [];

      if (listId) {
        // If listId is provided, fetch users from that specific list
        twitterUsernames = await ListsManager.fetchTwitterUsernamesFromList(listId);
        telegramChannelsList = await ListsManager.getTelegramChannels(listId);
        tiktokChannelsList = await ListsManager.getTikTokChannels(listId);
        instagramProfilesList = await ListsManager.getInstagramProfiles(listId);
      } else {
        // Otherwise fetch all users
        twitterUsernames = await ListsManager.fetchAllTwitterUsernames();
        telegramChannelsList = await ListsManager.fetchAllTelegramChannels();
        tiktokChannelsList = await ListsManager.fetchAllTikTokChannels();
        instagramProfilesList = await ListsManager.fetchAllInstagramProfiles();
      }

      setTwitterUsers(twitterUsernames);
      setTelegramChannels(telegramChannelsList);
      setTiktokChannels(tiktokChannelsList);
      setInstagramProfiles(instagramProfilesList);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      setError(t('app.filter.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserToggle = (user) => {
    const username = typeof user === 'string' ? user : user.username;

    if (!username) {
      console.error('No username available for toggle');
      return;
    }

    setSelectedUsers(prev => {
      if (prev.includes(username)) {
        return prev.filter(u => u !== username);
      } else {
        return [...prev, username];
      }
    });
  };

  const handleSourceToggle = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(s => s !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  const handleDateFilterSelect = (filter) => {
    setDateFilter(filter);

    // Set appropriate dates based on filter
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);

    setToDate(endOfDay);

    switch (filter) {
      case 'Today':
        const startOfDay = new Date(now);
        startOfDay.setHours(0, 0, 0, 0);
        setFromDate(startOfDay);
        break;
      case 'Last 7 days':
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        setFromDate(sevenDaysAgo);
        break;
      case 'Last 14 days':
        const fourteenDaysAgo = new Date(now);
        fourteenDaysAgo.setDate(now.getDate() - 14);
        setFromDate(fourteenDaysAgo);
        break;
      case 'Last 30 days':
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(now.getDate() - 30);
        setFromDate(thirtyDaysAgo);
        break;
      case 'Custom':
        // Keep existing dates or set defaults
        if (!fromDate) setFromDate(new Date(now.setDate(now.getDate() - 7)));
        if (!toDate) setToDate(endOfDay);
        break;
      default:
        setFromDate(null);
        setToDate(null);
    }
  };

  const handleApplyFilters = () => {
    const filterState = {
      filterApplied: true,
      listId,
      selectedUsers: selectedUsers.length > 0 ? selectedUsers : null,
      fromDate: fromDate?.toISOString(),
      toDate: toDate?.toISOString(),
      dateFilter,
      selectedSources: selectedSources.length > 0 ? selectedSources : null
    };

    if (onClose) {
      onClose(filterState);
    } else {
      // Navigate back to the previous screen with filter parameters
      navigate(-1, {
        state: filterState
      });
    }
  };

  const handleResetFilters = () => {
    setSelectedUsers([]);
    setFromDate(null);
    setToDate(null);
    setDateFilter(null);
    setSelectedSources([]);
  };

  const filterByUsername = (list, query) => {
    if (!query) return list;
    const lower = query.toLowerCase();
    return list.filter(item => {
      const username = typeof item === 'string' ? item : item.username;
      return username?.toLowerCase().includes(lower);
    });
  };

  const filteredTwitterUsers = filterByUsername(twitterUsers, searchText);
  const filteredTelegramChannels = filterByUsername(telegramChannels, searchText);
  const filteredTiktokChannels = filterByUsername(tiktokChannels, searchText);
  const filteredInstagramProfiles = filterByUsername(instagramProfiles, searchText);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={styles.loadingText}>{t('app.filter.loading')}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {!isModal && (
        <View style={styles.header}>
          <TouchableOpacity onPress={() => onClose ? onClose() : navigate(-1)}>
            <Text style={[styles.backButton, { transform: [{ scaleX: isRTL ? -1 : 1 }] }]}>← Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('app.filter.title')}</Text>
          <View style={{ width: 50 }} />
        </View>
      )}

      {error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : (
        <>
          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder={t('app.filter.searchPlaceholder')}
              placeholderTextColor="#888"
              value={searchText}
              onChangeText={setSearchText}
            />
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.sectionTitle}>{t('app.filter.dateRange')}</Text>
            <View style={styles.filterOptions}>
              {dateFilterOptions.map(option => (
                <TouchableOpacity
                  key={option.key}
                  style={[
                    styles.filterOption,
                    dateFilter === option.key && styles.filterOptionSelected
                  ]}
                  onPress={() => handleDateFilterSelect(option.key)}
                >
                  <Text style={[
                    styles.filterOptionText,
                    dateFilter === option.key && styles.filterOptionTextSelected
                  ]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.sectionTitle}>{t('app.filter.sources')}</Text>
            <View style={styles.filterOptions}>
              {sourceOptions.map(source => (
                <TouchableOpacity
                  key={source.id}
                  style={[
                    styles.filterOption,
                    selectedSources.includes(source.id) && styles.filterOptionSelected
                  ]}
                  onPress={() => handleSourceToggle(source.id)}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedSources.includes(source.id) && styles.filterOptionTextSelected
                  ]}>
                    {source.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.sectionTitle}>{t('app.filter.twitterUsers')}</Text>
            <View style={styles.usersContainer}>
              {filteredTwitterUsers.length > 0 ? (
                filteredTwitterUsers.map((user, index) => {
                  const username = typeof user === 'string' ? user : user.username;
                  return (
                    <TouchableOpacity
                      key={`twitter-${username}-${index}`}
                      style={[
                        styles.userOption,
                        selectedUsers.includes(username) && styles.userOptionSelected
                      ]}
                      onPress={() => handleUserToggle(user)}
                    >
                      <Text style={styles.userOptionText}>{username}</Text>
                      {selectedUsers.includes(username) && (
                        <Text style={styles.checkmark}>✓</Text>
                      )}
                    </TouchableOpacity>
                  );
                })
              ) : (
                <Text style={styles.noResultsText}>{t('app.filter.noTwitterUsers')}</Text>
              )}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.sectionTitle}>{t('app.filter.telegramChannels')}</Text>
            <View style={styles.usersContainer}>
              {filteredTelegramChannels.length > 0 ? (
                filteredTelegramChannels.map((channel, index) => {
                  const username = typeof channel === 'string' ? channel : channel.username;
                  return (
                    <TouchableOpacity
                      key={`telegram-${username}-${index}`}
                      style={[
                        styles.userOption,
                        selectedUsers.includes(username) && styles.userOptionSelected
                      ]}
                      onPress={() => handleUserToggle(channel)}
                    >
                      <Text style={styles.userOptionText}>{username}</Text>
                      {selectedUsers.includes(username) && (
                        <Text style={styles.checkmark}>✓</Text>
                      )}
                    </TouchableOpacity>
                  );
                })
              ) : (
                <Text style={styles.noResultsText}>{t('app.filter.noTelegramChannels')}</Text>
              )}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.sectionTitle}>{t('app.filter.tiktokChannels')}</Text>
            <View style={styles.usersContainer}>
              {filteredTiktokChannels.length > 0 ? (
                filteredTiktokChannels.map((channel, index) => {
                  const username = typeof channel === 'string' ? channel : channel.username;
                  return (
                    <TouchableOpacity
                      key={`tiktok-${username}-${index}`}
                      style={[
                        styles.userOption,
                        selectedUsers.includes(username) && styles.userOptionSelected
                      ]}
                      onPress={() => handleUserToggle(channel)}
                    >
                      <Text style={styles.userOptionText}>{username}</Text>
                      {selectedUsers.includes(username) && (
                        <Text style={styles.checkmark}>✓</Text>
                      )}
                    </TouchableOpacity>
                  );
                })
              ) : (
                <Text style={styles.noResultsText}>{t('app.filter.noTiktokChannels')}</Text>
              )}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.sectionTitle}>{t('app.filter.instagramProfiles')}</Text>
            <View style={styles.usersContainer}>
              {filteredInstagramProfiles.length > 0 ? (
                filteredInstagramProfiles.map((profile, index) => {
                  const username = typeof profile === 'string' ? profile : profile.username;
                  return (
                    <TouchableOpacity
                      key={`instagram-${username}-${index}`}
                      style={[
                        styles.userOption,
                        selectedUsers.includes(username) && styles.userOptionSelected
                      ]}
                      onPress={() => handleUserToggle(profile)}
                    >
                      <Text style={styles.userOptionText}>{username}</Text>
                      {selectedUsers.includes(username) && (
                        <Text style={styles.checkmark}>✓</Text>
                      )}
                    </TouchableOpacity>
                  );
                })
              ) : (
                <Text style={styles.noResultsText}>{t('app.filter.noInstagramProfiles')}</Text>
              )}
            </View>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.resetButton}
              onPress={handleResetFilters}
            >
              <Text style={styles.resetButtonText}>{t('app.filter.resetFilters')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.applyButton}
              onPress={handleApplyFilters}
            >
              <Text style={styles.applyButtonText}>{t('app.filter.applyFilters')}</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: 'var(--bg-card)',
    borderRadius: 20,
    marginBottom: 24,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  backButton: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorText: {
    color: 'var(--accent-warning)',
    textAlign: 'center',
    fontSize: 16,
  },
  searchContainer: {
    paddingBottom: 24,
  },
  searchInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: 'var(--text-primary)',
    padding: 16,
    borderRadius: 16,
    fontSize: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
    outlineStyle: 'none',
  },
  filterSection: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  sectionTitle: {
    color: 'var(--text-primary)',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterOption: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  filterOptionSelected: {
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    borderColor: 'var(--accent-primary)',
    boxShadow: '0 0 10px rgba(59, 130, 246, 0.2)',
  },
  filterOptionText: {
    color: 'var(--text-secondary)',
    fontSize: 14,
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: 'var(--text-primary)',
    fontWeight: '600',
  },
  usersContainer: {
    marginBottom: 8,
  },
  userOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--bg-card)',
    padding: 16,
    borderRadius: 16,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  userOptionSelected: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  userOptionText: {
    color: 'var(--text-primary)',
    fontSize: 16,
  },
  checkmark: {
    color: 'var(--accent-primary)',
    fontSize: 18,
    fontWeight: 'bold',
  },
  noResultsText: {
    color: 'var(--text-muted)',
    fontSize: 14,
    textAlign: 'center',
    marginVertical: 16,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
    marginTop: 16,
    gap: 16,
  },
  resetButton: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  resetButtonText: {
    color: 'var(--text-secondary)',
    fontSize: 15,
    fontWeight: '600',
  },
  applyButton: {
    flex: 1,
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 16,
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  applyButtonText: {
    color: 'white',
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: '0.02em',
  },
});

export default FilterScreen; 